/**
 * Helper untuk menafsirkan response form-config (GET form-config) versi baru.
 *
 * Bentuk response (data):
 * {
 *   tab_fields: { 
 *     "<Tab>": { 
 *       "<field>": { default, is_show, is_enabled, is_editable, is_mandatory, mandatory_condition, options_filter } 
 *     } 
 *   },
 *   description: string
 * }
 *
 * Untuk tab tertentu (organizational_data), struktur bisa nested:
 *   "organizational_data": {
 *     "fields": { "<field>": { ... } },
 *     "sections": { "<section_name>": { title, readonly, is_enabled, value_source } }
 *   }
 */

/** Normalisasi raw config jadi bentuk yang konsisten & aman dipakai. */
export function normalizeFormConfig(raw) {
	const data = raw?.data ?? raw ?? {};
	return {
		tab_fields: data.tab_fields && typeof data.tab_fields === 'object' ? data.tab_fields : {},
		description: data.description || '',
	};
}

/**
 * Mengambil field map dari sebuah tab, handle nested structure seperti organizational_data.
 * Jika tab data memiliki properti "fields" (nested), return fields tersebut.
 * Jika tidak, return tab data langsung.
 */
function getTabFieldMap(config, tab) {
	const tabKey = resolveTabKey(config, tab);
	const tabData = config?.tab_fields?.[tabKey];
	if (!tabData) return null;
	// Handle nested: tab data berisi { fields: {...}, sections: {...} }
	if (tabData.fields && typeof tabData.fields === 'object' && !Array.isArray(tabData.fields)) {
		return tabData.fields;
	}
	// Default: tab data langsung berisi field map
	return tabData;
}

/**
 * Pengecualian mapping nama tab "display" -> key backend.
 * Backend kini konsisten memakai snake_case dari nama tab (lihat `toSnakeKey`),
 * jadi yang perlu didaftarkan di sini HANYA tab yang key-nya tidak mengikuti pola itu.
 */
const TAB_KEY_ALIASES = {
	'MRP': 'emcr_mrp',
	'Manufacture': 'emcr_manufacturers',
	'Organization Level': 'organizational_data',
};

/** Ubah nama tab display menjadi snake_case key backend, mis. "Attachment of Material" -> "attachment_of_material". */
function toSnakeKey(tab) {
	return String(tab)
		.replace(/&/g, ' ')        // buang "&" (mis. "Basic Data & Specification")
		.trim()
		.toLowerCase()
		.replace(/\s+/g, '_');     // spasi (termasuk beruntun) jadi satu underscore
}

/**
 * Resolusi tab name menjadi key yang benar pada config.tab_fields.
 * Prioritas: key langsung -> alias pengecualian -> snake_case otomatis -> apa adanya.
 */
export function resolveTabKey(config, tab) {
	const tabs = config?.tab_fields || {};
	if (tab in tabs) return tab;

	const alias = TAB_KEY_ALIASES[tab];
	if (alias && alias in tabs) return alias;

	const snake = toSnakeKey(tab);
	if (snake in tabs) return snake;

	return tab;
}

/** Ambil config mentah satu field pada tab tertentu (atau null). */
export function getFieldConfig(config, tab, field) {
	const fields = getTabFieldMap(config, tab);
	return fields?.[field] ?? null;
}

/**
 * Apakah sebuah field ditampilkan (config-driven).
 * - field tidak ada di config -> tidak ditampilkan (false)
 * - is_show === false        -> tidak ditampilkan
 * - is_show berupa object     -> dievaluasi sebagai condition
 * - selain itu                -> ditampilkan
 */
export function isFieldVisible(config, tab, field, ctx) {
	// Jika tidak ada config sama sekali, fallback ke visible (backward compat)
	if (!config?.tab_fields) return true;
	const fc = getFieldConfig(config, tab, field);
	if (!fc) return false;
	if (fc.is_show === false) return false;
	if (fc.is_show && typeof fc.is_show === 'object') {
		return evalCondition(fc.is_show, ctx);
	}
	return true;
}

/** Apakah sebuah tab/section ditampilkan. */
export function isTabVisible(config, tab) {
	// Jika tidak ada config sama sekali, fallback ke visible (backward compat)
	if (!config?.tab_fields) return true;
	const fields = getTabFieldMap(config, tab);
	// Tab tidak ditemukan di config → hidden
	if (!fields) return false;
	// Tab dianggap visible jika setidaknya ada satu field yang is_show !== false
	return Object.values(fields).some(f => f.is_show !== false);
}

/**
 * Apakah sebuah section/tab punya minimal satu field yang mandatory (terhadap context).
 * Dipakai untuk menampilkan tanda * di header section secara config-driven.
 */
export function isSectionMandatory(config, tab, ctx) {
	const fields = getTabFieldMap(config, tab);
	if (!fields) return false;
	const tabKey = resolveTabKey(config, tab);
	return Object.keys(fields).some(field => isFieldMandatory(config, tabKey, field, ctx));
}

/** Apakah sebuah nilai dianggap "terisi" (untuk sentinel presence pada condition). */
function isFilledValue(v) {
	if (v === null || v === undefined) return false;
	if (typeof v === 'string') return v.trim() !== '';
	if (Array.isArray(v)) return v.length > 0;
	if (typeof v === 'object') {
		// AutoComplete/Dropdown: terisi jika ada value/code yang tidak kosong
		const inner = v.value !== undefined ? v.value : v.code;
		return inner !== undefined ? isFilledValue(inner) : Object.keys(v).length > 0;
	}
	return true;
}

/**
 * Evaluasi satu kondisi (is_enabled / mandatory_condition) terhadap context.
 *
 * Mendukung:
 * 1. { condition: { fieldName: valueOrArray, ... } } -> AND condition
 *    - value array         -> actual harus termasuk salah satunya
 *    - value `true` / `"filled"` -> field harus TERISI (presence check)
 *    - value `false` / `"empty"` -> field harus KOSONG
 *    - value lain          -> equality biasa
 * 2. { condition: { ... }, not: true } -> membalikkan hasil evaluasi condition
 * 3. { section: "...", operator: "has_line" }
 */
export function evalCondition(cond, ctx = {}) {
	if (!cond) return true;

	const getField = typeof ctx.getField === 'function' ? ctx.getField : () => undefined;
	const hasLine = typeof ctx.hasLine === 'function' ? ctx.hasLine : () => false;

	// Case 1: condition mapping { fieldName: values }
	if (cond.condition) {
		const result = Object.entries(cond.condition).every(([fieldName, expectedValue]) => {
			let actualValue = getField(fieldName);

			// Sentinel presence check: butuh nilai mentah (sebelum di-normalisasi)
			if (expectedValue === true || expectedValue === 'filled') {
				return isFilledValue(actualValue);
			}
			if (expectedValue === false || expectedValue === 'empty') {
				return !isFilledValue(actualValue);
			}

			// Handle object value (AutoComplete/Dropdown)
			if (actualValue && typeof actualValue === 'object') {
				actualValue = actualValue.value !== undefined ? actualValue.value : (actualValue.code !== undefined ? actualValue.code : actualValue);
			}

			if (Array.isArray(expectedValue)) {
				return expectedValue.includes(actualValue);
			}
			return actualValue === expectedValue;
		});

		// Dukungan `not`: membalikkan hasil condition
		if (cond.not) return !result;
		return result;
	}

	// Case 2: has_line logic
	if (cond.operator === 'has_line') {
		return !!hasLine(cond.section);
	}

	return true;
}

/**
 * Apakah field enabled (aktif).
 */
export function isFieldEnabled(config, tab, field, ctx) {
	const fc = getFieldConfig(config, tab, field);
	if (!fc) return true;
	
	if (fc.is_editable === false) return false;
	if (fc.is_show === false) return false;
	
	if (fc.is_enabled === false) return false;
	if (fc.is_enabled && typeof fc.is_enabled === 'object') {
		return evalCondition(fc.is_enabled, ctx);
	}
	
	return true;
}

/**
 * Apakah field wajib diisi.
 */
export function isFieldMandatory(config, tab, field, ctx) {
	const fc = getFieldConfig(config, tab, field);
	if (!fc) return false;
	
	// Jika field tidak enabled atau tidak tampil, maka tidak wajib
	if (!isFieldEnabled(config, tab, field, ctx)) return false;
	
	// Dukungan key `mandatory` (dipakai config MCR008 service) sebagai alias `is_mandatory`
	if (fc.is_mandatory === true || fc.mandatory === true) return true;
	if (fc.mandatory_condition) {
		return evalCondition(fc.mandatory_condition, ctx);
	}
	
	return false;
}

/** Nilai default sebuah field. */
export function getFieldDefault(config, tab, field, fallback = null) {
	const fc = getFieldConfig(config, tab, field);
	if (!fc || fc.default === undefined) return fallback;
	return fc.default;
}

/**
 * Ambil daftar VALUE opsi yang boleh tampil untuk sebuah field berdasarkan config `options_filter`.
 *
 * Format yang didukung (mapping field → { nilai → [allowed values] }):
 * 1. { condition: { level: { Plant: [...], AMPL: [...] } } }
 * 2. { level: { Plant: [...], AMPL: [...] } } (tanpa bungkus condition)
 *
 * Evaluasi: untuk tiap field pemicu, nilai aktual (dari ctx.getField) dicari di map.
 * - Semua field pemicu cocok → kembalikan irisan daftar allowed (jika > 1 field).
 * - Ada field pemicu yang tidak cocok (nilai aktual tidak ada di map) → null (serahkan fallback ke pemanggil).
 * - Tanpa options_filter → null.
 */
export function getFieldOptionsFilter(config, tab, field, ctx = {}) {
	const fc = getFieldConfig(config, tab, field);
	const filter = fc?.options_filter;
	if (!filter || typeof filter !== 'object') return null;

	const getField = typeof ctx.getField === 'function' ? ctx.getField : () => undefined;

	// Dukungan dua bentuk: { condition: {...} } atau mapping langsung
	const mapping = (filter.condition && typeof filter.condition === 'object')
		? filter.condition
		: filter;

	let allowed = null;
	for (const [triggerField, valueMap] of Object.entries(mapping)) {
		if (!valueMap || typeof valueMap !== 'object' || Array.isArray(valueMap)) continue;
		const actual = getField(triggerField);
		const values = valueMap[actual];
		// Field pemicu tidak cocok (mis. level kosong / nilai di luar map) → tidak bisa memfilter
		if (!Array.isArray(values)) return null;
		allowed = allowed === null ? values : allowed.filter(v => values.includes(v));
	}
	return allowed;
}

/**
 * Mendapatkan konfigurasi section dari tab yang memiliki nested sections (seperti organizational_data).
 * Contoh: getSectionConfig(config, 'Organization Level', 'copy_from') -> { title, readonly, is_enabled, ... }
 */
export function getSectionConfig(config, tab, sectionName) {
	const tabKey = resolveTabKey(config, tab);
	const tabData = config?.tab_fields?.[tabKey];
	if (!tabData?.sections || typeof tabData.sections !== 'object') return null;
	const sectionKey = Object.keys(tabData.sections).find(k => {
		const normalizedKey = k.toLowerCase().replace(/[^a-z0-9_]/g, '');
		const normalizedSection = sectionName.toLowerCase().replace(/[^a-z0-9_]/g, '');
		return normalizedKey === normalizedSection;
	});
	return sectionKey ? tabData.sections[sectionKey] : null;
}

/**
 * Apakah sebuah section di dalam tab (seperti copy_from) enabled berdasarkan config.
 */
export function isSectionEnabled(config, tab, sectionName, ctx) {
	const section = getSectionConfig(config, tab, sectionName);
	if (!section) return true;
	if (section.is_enabled && typeof section.is_enabled === 'object') {
		return evalCondition(section.is_enabled, ctx);
	}
	return section.is_enabled !== false;
}

/**
 * Apakah sebuah section di dalam tab bersifat readonly.
 */
export function isSectionReadonly(config, tab, sectionName) {
	const section = getSectionConfig(config, tab, sectionName);
	if (!section) return false;
	return section.readonly === true;
}

export default {
	normalizeFormConfig,
	resolveTabKey, // ini untuk parameter yang dilempar ke database (ref apidog), contohnya attachment_of_material.attachment
	isTabVisible,
	isFieldVisible,
	isSectionMandatory, // harusnya isTabMandatory, apabila salah satu field di dalam tab mandatiry dan ga diisi maka true
	getFieldConfig, // Untuk ambil data response mentah
	evalCondition,
	isFieldEnabled,
	isFieldMandatory,
	getFieldDefault,
	getFieldOptionsFilter, // untuk mapping data yang datanya dikit seperti standard description
	getSectionConfig,
	isSectionEnabled,
	isSectionReadonly,
};
